import { useMemo, useState } from "react"
import { ChevronRight } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { EmptyState } from "@/components/empty-state"
import { StatusBadge } from "@/components/status-badge"
import { Card } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import type {
  LoadDerivedStatus,
  MillFloorLoad,
  MillFloorQueueLot,
} from "@/features/mill-floor/api/types"
import {
  loadAssignedBagsActive,
  loadConsumedBags,
  loadCooperativeName,
  loadRemainingBags,
  qdlRowBagsForStatus,
  qdlRowKgForStatus,
  rowBagsForStatus,
  rowKgForStatus,
  rowBagsForTab,
  rowKgForTab,
} from "@/features/mill-floor/lib/load-math"
import { loadDerivedTone } from "@/features/mill-floor/lib/status-tones"
import { formatMt, formatNumber, kgToMt } from "@/lib/format"
import { paths } from "@/routes/paths"

const HEAD_CELL =
  "h-11 px-4 text-muted-foreground text-xs font-medium uppercase tracking-wide"
const BODY_CELL = "px-4 py-3"

// Top-level tab values. The first four scope the chargements list by
// derived status; "queue-de-lots" switches to the QDL stream entirely
// (no further sub-filter — its own row count is the operator-visible
// number).
type WarehouseTab = "all" | LoadDerivedStatus | "queue-de-lots"

const LOAD_TABS: ("all" | LoadDerivedStatus)[] = [
  "all",
  "available",
  "assigned",
  "consumed",
]

interface WarehouseCardProps {
  loads: MillFloorLoad[]
  queueLots: MillFloorQueueLot[]
  isLoading?: boolean
}

export function WarehouseCard({
  loads,
  queueLots,
  isLoading,
}: WarehouseCardProps) {
  const { t } = useTranslation("milling")
  const navigate = useNavigate()
  const [tab, setTab] = useState<WarehouseTab>("available")

  // Counts are computed via per-tab predicates rather than a single
  // derivedStatus enum so a load with 300 free + 900 assigned bumps
  // BOTH the Available and Assigned counts (and appears in both
  // tabs). "All" remains the unique-load count.
  const counts = useMemo(() => {
    let available = 0
    let assigned = 0
    let consumed = 0
    for (const l of loads) {
      if (loadRemainingBags(l) > 0) available += 1
      if (loadAssignedBagsActive(l) > 0) assigned += 1
      if (loadConsumedBags(l) > 0) consumed += 1
    }
    return { all: loads.length, available, assigned, consumed }
  }, [loads])

  const visibleLoads = useMemo(() => {
    if (tab === "queue-de-lots") return []
    if (tab === "all") return loads
    return loads.filter((l) => {
      switch (tab) {
        case "available":
          return loadRemainingBags(l) > 0
        case "assigned":
          return loadAssignedBagsActive(l) > 0
        case "consumed":
          return loadConsumedBags(l) > 0
      }
    })
  }, [loads, tab])

  const visibleQdls = useMemo(() => {
    if (tab !== "queue-de-lots") return []
    return queueLots
  }, [queueLots, tab])

  // Totals respect the tab context: under Assigned we sum the
  // assigned slice of each row, not its whole stock. Under "all" we
  // fall back to the row's primary (derivedStatus) slice so the
  // header doesn't double-count loads that span buckets.
  const totals = useMemo(() => {
    let bags = 0
    let kg = 0
    if (tab === "queue-de-lots") {
      for (const q of visibleQdls) {
        bags += qdlRowBagsForStatus(q)
        kg += qdlRowKgForStatus(q)
      }
    } else if (tab === "all") {
      for (const l of visibleLoads) {
        bags += rowBagsForStatus(l)
        kg += rowKgForStatus(l)
      }
    } else {
      for (const l of visibleLoads) {
        bags += rowBagsForTab(l, tab)
        kg += rowKgForTab(l, tab)
      }
    }
    return { bags, mt: kgToMt(kg) }
  }, [visibleLoads, visibleQdls, tab])

  const rowCount =
    tab === "queue-de-lots" ? visibleQdls.length : visibleLoads.length

  const emptyKey =
    tab === "queue-de-lots"
      ? "millFloor.warehouse.emptyQueueLots"
      : tab === "all"
        ? "millFloor.warehouse.emptyAll"
        : tab === "available"
          ? "millFloor.warehouse.emptyAvailable"
          : tab === "assigned"
            ? "millFloor.warehouse.emptyAssigned"
            : "millFloor.warehouse.emptyConsumed"

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight">
          {t("millFloor.warehouse.title")}
        </h2>
        <div className="flex flex-wrap items-baseline gap-4 text-xs text-muted-foreground">
          <span>
            <span className="mr-1 text-foreground tabular-nums">
              {rowCount}
            </span>
            {t("millFloor.warehouse.loads")}
          </span>
          <span>
            <span className="mr-1 text-foreground tabular-nums">
              {formatNumber(totals.bags)}
            </span>
            {t("millFloor.warehouse.bags")}
          </span>
          <span>
            <span className="mr-1 text-foreground tabular-nums">
              {formatNumber(totals.mt, 2)}
            </span>
            {t("millFloor.warehouse.mt")}
          </span>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as WarehouseTab)}>
        <TabsList variant="line" className="justify-start gap-6">
          {LOAD_TABS.map((key) => (
            <TabsTrigger key={key} value={key}>
              {t(`millFloor.warehouse.tabs.${key}`)}
              <span className="ml-1 text-muted-foreground tabular-nums">
                ({counts[key]})
              </span>
            </TabsTrigger>
          ))}
          <TabsTrigger value="queue-de-lots">
            {t("millFloor.warehouse.tabs.queue-de-lots")}
            <span className="ml-1 text-muted-foreground tabular-nums">
              ({queueLots.length})
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="pt-4">
          {rowCount === 0 ? (
            <EmptyState
              title={t(emptyKey)}
              description={isLoading ? "…" : undefined}
            />
          ) : tab === "queue-de-lots" ? (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableHead className={HEAD_CELL}>
                      {t("millFloor.warehouse.headers.qdlId")}
                    </TableHead>
                    <TableHead className={HEAD_CELL}>
                      {t("millFloor.warehouse.headers.status")}
                    </TableHead>
                    <TableHead className={HEAD_CELL}>
                      {t("millFloor.warehouse.headers.usinage")}
                    </TableHead>
                    <TableHead className={`${HEAD_CELL} text-right`}>
                      {t("millFloor.warehouse.headers.bagsMT")}
                    </TableHead>
                    <TableHead aria-hidden className={`${HEAD_CELL} w-8`} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleQdls.map((qdl) => (
                    <QueueLotWarehouseRow key={qdl.id} qdl={qdl} />
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableHead className={HEAD_CELL}>
                      {t("millFloor.warehouse.headers.truck")}
                    </TableHead>
                    <TableHead className={HEAD_CELL}>
                      {t("millFloor.warehouse.headers.cooperative")}
                    </TableHead>
                    <TableHead className={HEAD_CELL}>
                      {t("millFloor.warehouse.headers.status")}
                    </TableHead>
                    <TableHead className={HEAD_CELL}>
                      {t("millFloor.warehouse.headers.usinage")}
                    </TableHead>
                    <TableHead className={`${HEAD_CELL} text-right`}>
                      {t("millFloor.warehouse.headers.bagsMT")}
                    </TableHead>
                    <TableHead aria-hidden className={`${HEAD_CELL} w-8`} />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleLoads.map((load) => (
                    <WarehouseRow
                      key={load.id}
                      load={load}
                      tabContext={tab === "all" ? null : tab}
                      onOpen={() =>
                        navigate(paths.receptionDetail(load.id))
                      }
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </Card>
  )
}

function WarehouseRow({
  load,
  tabContext,
  onOpen,
}: {
  load: MillFloorLoad
  // null when the row is rendered in the "All" tab — we then fall
  // back to the load's own derivedStatus for chip / eyebrow / amount.
  // Otherwise the tab forces the slice: under "Assigned" we show the
  // assigned bags and the assigned chip, even if the load also has
  // bags free in the warehouse.
  tabContext: LoadDerivedStatus | null
  onOpen: () => void
}) {
  const { t } = useTranslation("milling")
  const effectiveStatus: LoadDerivedStatus = tabContext ?? load.derivedStatus
  const tone = loadDerivedTone(effectiveStatus)

  const bags = tabContext
    ? rowBagsForTab(load, tabContext)
    : rowBagsForStatus(load)
  const kg = tabContext ? rowKgForTab(load, tabContext) : rowKgForStatus(load)

  const eyebrowKey =
    effectiveStatus === "available"
      ? "millFloor.warehouse.remaining"
      : effectiveStatus === "assigned"
        ? "millFloor.warehouse.assignedLabel"
        : "millFloor.warehouse.consumedLabel"

  // Usinage column is only meaningful in the assigned slice — under
  // "Available" the bags being shown are precisely those NOT tied to
  // a usinage, so showing the active run there would be misleading.
  const usinageNumber =
    effectiveStatus === "assigned"
      ? (load.activeUsinage?.usinageNumber ?? "—")
      : "—"

  return (
    <TableRow className="cursor-pointer hover:bg-muted/60" onClick={onOpen}>
      <TableCell className={`${BODY_CELL} font-mono text-xs tabular-nums`}>
        {load.plateNumber ??
          load.DeliveryForms?.truckRegistrationNumber ??
          `#${load.loadNumber}`}
      </TableCell>
      <TableCell className={BODY_CELL}>
        {loadCooperativeName(load) ?? "—"}
      </TableCell>
      <TableCell className={BODY_CELL}>
        <StatusBadge tone={tone}>
          {t(`millFloor.warehouse.statusChip.${effectiveStatus}`)}
        </StatusBadge>
      </TableCell>
      <TableCell
        className={`${BODY_CELL} font-mono text-xs text-muted-foreground`}
      >
        {usinageNumber}
      </TableCell>
      <TableCell className={`${BODY_CELL} text-right`}>
        <div className="text-[10px] tracking-wide text-muted-foreground uppercase">
          {t(eyebrowKey)}
        </div>
        <div className="tabular-nums">
          {t("millFloor.warehouse.rowBagsMT", {
            bags,
            mt: formatMt(kg),
          })}
        </div>
      </TableCell>
      <TableCell className={BODY_CELL}>
        <ChevronRight className="size-4 text-muted-foreground" />
      </TableCell>
    </TableRow>
  )
}

// QueueLotWarehouseRow renders one row of the QDL tab. QDLs don't
// have a truck / plate / cooperative — identity is the lot number;
// the "Usinage" column surfaces the originating usinage (provenance),
// not any active re-coupling reservation.
function QueueLotWarehouseRow({ qdl }: { qdl: MillFloorQueueLot }) {
  const { t } = useTranslation("milling")
  const tone = loadDerivedTone(qdl.derivedStatus)
  const bags = qdlRowBagsForStatus(qdl)
  const kg = qdlRowKgForStatus(qdl)

  const eyebrowKey =
    qdl.derivedStatus === "available"
      ? "millFloor.warehouse.remaining"
      : qdl.derivedStatus === "assigned"
        ? "millFloor.warehouse.assignedLabel"
        : "millFloor.warehouse.consumedLabel"

  // Provenance — the run that fabricated this QDL. Always set on the
  // backend; we fall back to activeUsinage only if the embed is
  // missing for some reason (defensive — shouldn't normally happen).
  const usinageNumber =
    qdl.originUsinage?.usinageNumber ??
    qdl.activeUsinage?.usinageNumber ??
    "—"

  return (
    <TableRow className="hover:bg-muted/60">
      <TableCell className={`${BODY_CELL} font-mono text-xs tabular-nums`}>
        {qdl.lotNumber ?? `#${qdl.id}`}
      </TableCell>
      <TableCell className={BODY_CELL}>
        <StatusBadge tone={tone}>
          {t(`millFloor.warehouse.statusChip.${qdl.derivedStatus}`)}
        </StatusBadge>
      </TableCell>
      <TableCell
        className={`${BODY_CELL} font-mono text-xs text-muted-foreground`}
      >
        {usinageNumber}
      </TableCell>
      <TableCell className={`${BODY_CELL} text-right`}>
        <div className="text-[10px] tracking-wide text-muted-foreground uppercase">
          {t(eyebrowKey)}
        </div>
        <div className="tabular-nums">
          {t("millFloor.warehouse.rowBagsMT", {
            bags,
            mt: formatMt(kg),
          })}
        </div>
      </TableCell>
      <TableCell className={BODY_CELL} />
    </TableRow>
  )
}
